import type {
    RepeatableGroupConfiguration,
    RepeatablePendingDropdownResolutionConfiguration,
    RepeatableSubField,
} from '../../../interfaces/input.interface';
import type {
    RepeatablePendingDropdownResolutionPolicy,
    RepeatableRowChange,
    RepeatableRowValue,
} from './repeatable-group.types';
import { RepeatableValueUtils } from './repeatable-value.utils';

/**
 * Pure repeatable-group rules that do not need Angular templates or component state.
 *
 * Keep orchestration, subscriptions and rendering in RepeatableGroupComponent, but place row comparison,
 * dependency discovery and row-count policy here so they can be tested without TestBed.
 */
export class RepeatableGroupEngine {
    public static normalizeMaxRows(rows: number | string | null | undefined): number {
        const maxRows = Number(rows ?? 1);

        return Number.isFinite(maxRows) && maxRows > 0 ? maxRows : 1;
    }


    public static normalizePendingDropdownResolutionPolicy(
        configuration: RepeatablePendingDropdownResolutionConfiguration | null | undefined,
    ): RepeatablePendingDropdownResolutionPolicy {
        return {
            maxAttempts: this.normalizePositiveInteger(configuration?.maxAttempts, 200),
            retryDelayMs: this.normalizePositiveInteger(configuration?.retryDelayMs, 50),
            cascadePassLimit: this.normalizePositiveInteger(configuration?.cascadePassLimit, 25),
            stalledAttemptLimit: this.normalizePositiveInteger(configuration?.stalledAttemptLimit, 3),
        };
    }

    public static canAddRow(isEditable: boolean, currentCount: number, maxRows: number): boolean {
        return isEditable && currentCount < maxRows;
    }

    public static canDeleteRow(isEditable: boolean, currentCount: number): boolean {
        return isEditable && currentCount > 1;
    }

    public static getRowValueChanges(previousRows: RepeatableRowValue[], currentRows: RepeatableRowValue[]): RepeatableRowChange[] {
        const changes: RepeatableRowChange[] = [];
        const maxRows = Math.max(previousRows.length, currentRows.length);

        for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
            changes.push(...this.getSingleRowValueChanges(rowIndex, previousRows[rowIndex] ?? {}, currentRows[rowIndex] ?? {}));
        }

        return changes;
    }

    public static getDependencyKeys(subField: RepeatableSubField): string[] {
        return this.toStringArray(subField.dependsOn);
    }

    public static getParentDependencyKeys(
        configuration: RepeatableGroupConfiguration,
        subField?: RepeatableSubField,
    ): string[] {
        const keys = new Set<string>([
            ...this.toStringArray(configuration.parentDependencies),
            ...this.toStringArray(subField?.parentDependencies),
        ].map((key) => key.trim()).filter((key) => key !== ''));

        return Array.from(keys);
    }

    public static getAllDependencyKeys(
        configuration: RepeatableGroupConfiguration,
        subField: RepeatableSubField,
    ): string[] {
        return [
            ...this.getDependencyKeys(subField),
            ...this.getParentDependencyKeys(configuration, subField),
        ];
    }

    public static getDependentSubFields(subFields: RepeatableSubField[], changedKey: string): RepeatableSubField[] {
        return subFields.filter((subField) => this.getDependencyKeys(subField).includes(changedKey));
    }

    public static getInitialDependencyChangesForRows(
        subFields: RepeatableSubField[],
        rows: RepeatableRowValue[],
    ): RepeatableRowChange[] {
        const dependencyKeys = this.getUniqueRowDependencyKeys(subFields);

        if (dependencyKeys.length === 0) {
            return [];
        }

        return rows
            .flatMap((row, rowIndex) => dependencyKeys.map((key) => ({
                rowIndex,
                key,
                previousValue: null,
                currentValue: row[key],
            })))
            .filter((change) => RepeatableValueUtils.hasNonEmptyValue(change.currentValue));
    }

    public static toStringArray(value: string | readonly string[] | undefined): string[] {
        if (!value) {
            return [];
        }

        return typeof value === 'string' ? [value] : [...value];
    }

    private static normalizePositiveInteger(value: number | null | undefined, fallback: number): number {
        const candidate = Number(value ?? fallback);

        return Number.isFinite(candidate) && candidate > 0 ? Math.floor(candidate) : fallback;
    }

    private static getSingleRowValueChanges(
        rowIndex: number,
        previousRow: RepeatableRowValue,
        currentRow: RepeatableRowValue,
    ): RepeatableRowChange[] {
        const changes: RepeatableRowChange[] = [];
        const keys = new Set([...Object.keys(previousRow), ...Object.keys(currentRow)]);

        for (const key of keys) {
            const previousValue = previousRow[key];
            const currentValue = currentRow[key];

            if (!RepeatableValueUtils.areEquivalentCellValues(previousValue, currentValue)) {
                changes.push({ rowIndex, key, previousValue, currentValue });
            }
        }

        return changes;
    }

    private static getUniqueRowDependencyKeys(subFields: RepeatableSubField[]): string[] {
        const dependencyKeys = new Set<string>();

        for (const subField of subFields) {
            for (const dependencyKey of this.getDependencyKeys(subField)) {
                dependencyKeys.add(dependencyKey);
            }
        }

        return Array.from(dependencyKeys);
    }
}
